/**
 * The single definition of "this user opted in to Slack notifications", shared by the desktop
 * and mobile profile pages and matching Pulsar's `SlackChannelStrategy.isEnabled`.
 *
 * Opted in means BOTH `notificationSettings.alertCenter.slack === true` AND a linked Slack
 * identity. The identity is written only by Orbit `LinkSlackIdentity`, which the UI calls when
 * the user ticks the Slack checkbox, so the pair records an explicit choice. The flag on its own
 * does not: most stored `slack: true` values were written by older clients that defaulted it on
 * before the user had any Slack control to see. Treating the flag alone as an opt-in would show
 * those users a pre-ticked box and, through linking, start Slack messages they never chose.
 *
 * Structurally typed so both the legacy Redux preferences and the generated v2 client shape fit.
 */
// `| undefined` on every optional member keeps the generated client types (which declare optional
// members as `T | undefined`) assignable under exactOptionalPropertyTypes.
export interface SlackOptInPreferences {
  notificationSettings?:
    | { alertCenter?: { slack?: boolean | undefined } | undefined }
    | undefined;
  slackIdentity?: { slackUserId?: string | undefined } | undefined;
}

export function isSlackOptedIn(
  preferences: SlackOptInPreferences | null | undefined,
): boolean {
  return (
    preferences?.notificationSettings?.alertCenter?.slack === true &&
    Boolean(preferences.slackIdentity?.slackUserId?.trim())
  );
}
